import { useEffect, useRef } from 'react'
import { useAuth } from './AuthContext'

/**
 * Activity-based token refresh hook
 *
 * Enterprise security features:
 * - Only refreshes on actual user activity (mouse, keyboard, touch)
 * - Prevents refresh spam with 5-minute cooldown
 * - 30-minute inactivity timeout (token expires without activity)
 * - 8-hour absolute maximum session (enforced by backend)
 *
 * How it works:
 * 1. Listens for user activity events
 * 2. Updates lastActivityTime on any interaction
 * 3. Checks every minute if token needs refresh
 * 4. Only refreshes if:
 *    - User was active in the last 25 minutes (5-minute buffer before 30-min expiry)
 *    - Last refresh was more than 5 minutes ago (prevents spam)
 */
export function useActivityBasedRefresh() {
  const { isAuthenticated, refreshToken } = useAuth()
  const lastActivityTime = useRef<number>(Date.now())
  const lastRefreshTime = useRef<number>(Date.now())

  useEffect(() => {
    if (!isAuthenticated) return

    // Activity detection events
    const activityEvents = ['mousedown', 'keydown', 'scroll', 'touchstart']

    const updateActivity = () => {
      lastActivityTime.current = Date.now()
    }

    // Register activity listeners
    activityEvents.forEach(event => {
      window.addEventListener(event, updateActivity, { passive: true })
    })

    // Check every minute if we need to refresh the token
    const checkInterval = setInterval(async () => {
      const now = Date.now()
      const timeSinceActivity = now - lastActivityTime.current
      const timeSinceRefresh = now - lastRefreshTime.current

      // Only refresh if:
      // 1. User was active in the last 25 minutes (5-minute buffer before 30-min expiry)
      // 2. Last refresh was more than 5 minutes ago (prevents refresh spam)
      const shouldRefresh =
        timeSinceActivity < 25 * 60 * 1000 && // Active within 25 minutes
        timeSinceRefresh > 5 * 60 * 1000       // Last refresh was over 5 minutes ago

      if (shouldRefresh) {
        console.log('[Activity Refresh] Refreshing token due to user activity')
        const success = await refreshToken()
        if (success) {
          lastRefreshTime.current = now
        }
      }
    }, 60 * 1000) // Check every minute

    return () => {
      activityEvents.forEach(event => {
        window.removeEventListener(event, updateActivity)
      })
      clearInterval(checkInterval)
    }
  }, [isAuthenticated, refreshToken])
}
